"use client"

import { PostAJobModal } from "@/components/PostAJobModal"
import { useJobUpdateData } from "@/components/PostAJobModal/useJobUpdateData"
import { Spinner } from "@/elements"
import { graphql } from "@/gql"
import { MyJobsDraftJobPostsQueryDocument } from "@/gql/graphql"
import { useHandleListEndReached } from "@/hooks/useHandleListEndReached"
import type { NovaSession } from "@/types"
import { useQuery } from "@apollo/client"
import { useRef, useState } from "react"
import { DraftJobsMobile } from "./DraftJobsMobile"
import DraftJobsTable from "./DraftJobsTable"

graphql(`
  query MyJobsDraftJobPostsQuery($first: Int, $after: String, $id: ID!, $isClient: Boolean!) {
    node(id: $id) {
      id
      __typename
      ... on IndividualCreative {
        jobPostings(status: DRAFT, first: $first, after: $after) @skip(if: $isClient) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              id
              ...DraftJobPosting
            }
          }
        }
      }

      ... on IndividualClient {
        jobPostings(status: DRAFT, first: $first, after: $after) @include(if: $isClient) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              id
              ...DraftJobPosting
            }
          }
        }
      }
    }
  }
`)

graphql(`
  fragment UpdateJobPostData on JobPosting {
    __typename
    id
    employmentType
    employmentTypeDisplay
    location
    locationName
    locationType
    locationTypeDisplay
    title
    position {
      id
      name
    }
    creativeWorkType {
      id
      name
      indefiniteArticle
    }
    skills {
      edges {
        node {
          id
          name
        }
      }
    }
    coverImage {
      id
      thumbnailUrl645: thumbnailUrl(width: 645)
    }
    payType
    budget {
      currency
      amount
    }
    pay {
      currency
      amount
    }
    budgetMax {
      currency
      amount
    }
    payMax {
      currency
      amount
    }
    payRate
    payString
    startDate
    endDate
    datesAreFlexible
    description
    payDisplay
  }
`)

graphql(`
  query CreativeUpdateJobPostData($jobPostId: ID!) {
    jobPost: creativeJobPosting(id: $jobPostId) {
      id
      __typename
      ...UpdateJobPostData
    }
  }
`)

graphql(`
  query ClientUpdateJobPostData($jobPostId: ID!) {
    jobPost: clientJobPosting(id: $jobPostId) {
      id
      __typename
      ...UpdateJobPostData
    }
  }
`)

graphql(`
  fragment DraftJobPosting on JobPosting {
    id
    createdAt
    locationName
    locationType
    startDate
    endDate
    datesAreFlexible
    position {
      id
      name
    }
    ...JobTitleColorised
  }
`)

interface Props {
  currentUserField: "client" | "creative"
  serverSession: NovaSession
}

const PAGE_SIZE = 20

export const DraftJobs: React.FC<Props> = ({ currentUserField, serverSession }) => {
  const { data, loading, networkStatus, fetchMore } = useQuery(MyJobsDraftJobPostsQueryDocument, {
    variables: {
      first: PAGE_SIZE,
      after: null,
      id: (currentUserField === "creative" ? serverSession.user.creative.id : serverSession.user.client.id)!,
      isClient: currentUserField === "client",
    },
  })
  const { getUpdateData, updateData, loadingUpdateData } = useJobUpdateData({ serverSession })

  const [showPostAJobModal, setShowPostAJobModal] = useState(false)

  const onClickRow = (jobPostId: string) => {
    getUpdateData({
      variables: {
        jobPostId,
      },
    })
    setShowPostAJobModal(true)
  }

  const list =
    data?.node?.__typename === "IndividualClient" || data?.node?.__typename === "IndividualCreative"
      ? data?.node?.jobPostings
      : null

  const loadMoreRef = useRef(null)
  useHandleListEndReached({
    pageInfo: list?.pageInfo,
    first: PAGE_SIZE,
    fetchMore,
    networkStatus,
    observerRef: loadMoreRef,
  })

  return (
    <>
      <PostAJobModal
        open={showPostAJobModal}
        onClose={() => setShowPostAJobModal(false)}
        serverSession={serverSession}
        loading={loadingUpdateData}
        updateData={updateData}
        entryPoint="draft-jobs"
      />
      <DraftJobsTable data={data} loading={loading} onClickRow={onClickRow} />
      <DraftJobsMobile data={data} loading={loading} onClickRow={onClickRow} />
      <div className="relative mb-1 mt-2 flex h-[20px] w-full flex-row items-center justify-center" ref={loadMoreRef}>
        {list?.pageInfo?.hasNextPage && <Spinner size="medium" />}
      </div>
    </>
  )
}
